import { useTranslation } from "react-i18next";
import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { useGetLeaveRequestStatQuery } from "@/services/(company)/leaveRequestApi";
import {
  MetricValue,
  MiniMetric,
  MiniStat,
  Sparkbars,
  WidgetNoAccess,
} from "./primitives";

/**
 * The leave request funnel. Pending is the headline at every size because it is
 * the only figure that implies work; the rest is context that appears as the
 * widget grows.
 */
export const LeaveRequestsWidget = ({ size }: IWidgetRenderContext) => {
  const { t } = useTranslation(["common", "company", "nav"]);
  const { data, isLoading, error } = useGetLeaveRequestStatQuery();

  if (error) return <WidgetNoAccess error={error} />;

  const stats = data?.data;

  // The card draws the chip and the title at every size, this one included, so
  // there is nothing here to add beyond the number.
  if (size === "xs") {
    return (
      <MiniMetric
        value={stats?.pendingLeaveRequests ?? 0}
        isLoading={isLoading}
      />
    );
  }

  if (size === "sm") {
    return (
      <div className="flex h-full flex-col justify-center gap-0.5">
        <MetricValue
          value={stats?.pendingLeaveRequests ?? 0}
          isLoading={isLoading}
        />
        <p className="text-[11px] text-muted-foreground">
          {stats?.newPendingToday
            ? t("company:widgets.pending_with_new_today", {
                count: stats.newPendingToday,
              })
            : t("company:widgets.pending_lowercase")}
        </p>
      </div>
    );
  }

  // Same width as `sm`, stood up. The Sparkbars shape `lg` draws needs the wide
  // sizes' width to read as five distinct bars rather than five slivers, so the
  // narrow-tall column gets the same three figures stacked instead.
  if (size === "tall") {
    return (
      <div className="flex h-full flex-col justify-between gap-2">
        <div>
          <MetricValue
            value={stats?.pendingLeaveRequests ?? 0}
            isLoading={isLoading}
          />
          <p className="text-[11px] text-muted-foreground">
            {t("company:widgets.pending_approval")}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 border-t border-border pt-2">
          <MiniStat
            label={t("company:widgets.new_today")}
            tone="primary"
            value={stats?.newPendingToday ?? 0}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:widgets.approved_today")}
            tone="success"
            value={stats?.approvedToday ?? 0}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:widgets.rejected_per_week")}
            tone="danger"
            value={stats?.rejectedThisWeek ?? 0}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-between gap-2">
      <div className="flex items-end gap-3">
        <MetricValue
          value={stats?.pendingLeaveRequests ?? 0}
          isLoading={isLoading}
          scale="xl"
        />
        <p className="pb-1 text-[11px] text-muted-foreground">
          {t("company:widgets.pending_approval")}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MiniStat
          label={t("company:widgets.new_today")}
          tone="primary"
          value={stats?.newPendingToday ?? 0}
          isLoading={isLoading}
        />
        <MiniStat
          label={t("company:widgets.approved_today")}
          tone="success"
          value={stats?.approvedToday ?? 0}
          isLoading={isLoading}
        />
        <MiniStat
          label={t("company:widgets.rejected_per_week")}
          tone="danger"
          value={stats?.rejectedThisWeek ?? 0}
          isLoading={isLoading}
        />
      </div>

      {size === "lg" && (
        // The four figures the endpoint gives are stages of one funnel, so at the
        // tall size they get drawn as a shape rather than repeated as numbers.
        <div className="min-h-0 flex-1 border-t border-border pt-2">
          <Sparkbars
            tone="primary"
            isLoading={isLoading}
            series={[
              {
                label: t("company:widgets.new"),
                value: stats?.newPendingToday ?? 0,
              },
              {
                label: t("company:widgets.pending"),
                value: stats?.pendingLeaveRequests ?? 0,
              },
              {
                label: t("company:widgets.appr_short"),
                value: stats?.approvedToday ?? 0,
              },
              {
                label: t("company:widgets.rej_short"),
                value: stats?.rejectedThisWeek ?? 0,
              },
              {
                label: t("company:widgets.month"),
                value: stats?.totalLeavesThisMonth ?? 0,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};
